import React, { useState, useEffect, useRef, useCallback } from "react";
import { Spinner, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../AuthContext";
import { supportApi } from "../../services/supportApi";
import SupportMessage from "./SupportMessage";
import SupportUploader from "./SupportUploader";

const POLL_INTERVAL_MS = 5000;

const STATUS_OPTIONS = ["open", "in_progress", "resolved", "closed"];

const STATUS_COLOR = {
  open: { bg: "#eef2ff", color: "#6366f1" },
  in_progress: { bg: "#fff7ed", color: "#f59e0b" },
  resolved: { bg: "#f0fdf4", color: "#10b981" },
  closed: { bg: "#f3f4f6", color: "#9ca3af" },
};

const SupportChat = ({ ticketId, isAdmin, onTicketUpdated }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);
  const statusMenuRef = useRef(null);

  const fetchTicket = useCallback(async () => {
    try {
      const fetch = isAdmin
        ? supportApi.adminGetTicket(ticketId)
        : supportApi.getTicket(ticketId);
      const res = await fetch;
      setTicket(res.data.ticket);
      setMessages(res.data.messages || []);
      setError(null);
    } catch (err) {
      setError("Failed to load conversation.");
    } finally {
      setLoading(false);
    }
  }, [ticketId, isAdmin]);

  useEffect(() => {
    setLoading(true);
    setMessages([]);
    setTicket(null);
    fetchTicket();
    pollRef.current = setInterval(fetchTicket, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchTicket]);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Close status dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (statusMenuRef.current && !statusMenuRef.current.contains(e.target)) {
        setShowStatusMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    setError(null);
    try {
      if (isAdmin) {
        await supportApi.adminPostMessage(ticketId, body.trim(), files);
      } else {
        await supportApi.postMessage(ticketId, body.trim(), files);
      }
      setBody("");
      setFiles([]);
      await fetchTicket();
      if (onTicketUpdated) onTicketUpdated();
    } catch (err) {
      setError("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (status) => {
    setShowStatusMenu(false);
    setUpdatingStatus(true);
    try {
      await supportApi.adminUpdateStatus(ticketId, status);
      setTicket((prev) => ({ ...prev, status }));
      if (onTicketUpdated) onTicketUpdated();
    } catch {
      setError("Failed to update status.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
        <Spinner size="sm" style={{ color: "#6366f1" }} />
      </div>
    );
  }

  if (error && !ticket) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", fontSize: "13px", color: "#ef4444" }}>
        {error}
      </div>
    );
  }

  const isClosed = ticket?.status === "closed";
  const statusStyle = STATUS_COLOR[ticket?.status] || { bg: "#f3f4f6", color: "#9ca3af" };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>

      {/* Header */}
      <div style={{
        padding: "14px 20px",
        borderBottom: "1px solid #f3f4f6",
        display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        flexWrap: "wrap", gap: "10px",
        background: "#fff",
      }}>
        <div>
          <div style={{ fontSize: "14px", fontWeight: 700, color: "#111827" }}>
            {ticket?.subject}
          </div>
          {isAdmin && ticket?.user_email && (
            <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}>
              {t("support.ticket_from")}: {ticket.user_firstname ? `${ticket.user_firstname} ${ticket.user_lastname || ""}`.trim() : ""} &lt;{ticket.user_email}&gt;
            </div>
          )}
        </div>

        {isAdmin && ticket && (
          <div ref={statusMenuRef} style={{ position: "relative" }}>
            <button
              onClick={() => setShowStatusMenu((v) => !v)}
              disabled={updatingStatus}
              style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                padding: "5px 12px", borderRadius: "8px",
                background: statusStyle.bg, color: statusStyle.color,
                border: `1px solid ${statusStyle.color}22`,
                fontSize: "11px", fontWeight: 700,
                textTransform: "uppercase", letterSpacing: "0.04em",
                cursor: "pointer", transition: "all 0.15s",
              }}
            >
              {updatingStatus ? <Spinner size="sm" /> : (t(`support.status_${ticket.status}`) || ticket.status)}
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
            </button>
            {showStatusMenu && (
              <div style={{
                position: "absolute", right: 0, top: "calc(100% + 4px)",
                background: "#fff", borderRadius: "10px",
                border: "1px solid #e5e7eb",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                zIndex: 100, minWidth: "140px", overflow: "hidden",
              }}>
                {STATUS_OPTIONS.map((s) => {
                  const sc = STATUS_COLOR[s] || { bg: "#f3f4f6", color: "#9ca3af" };
                  return (
                    <button
                      key={s}
                      onClick={() => handleStatusChange(s)}
                      style={{
                        display: "block", width: "100%", textAlign: "left",
                        padding: "9px 14px", border: "none",
                        background: ticket.status === s ? "#f9fafb" : "#fff",
                        fontSize: "12px", fontWeight: ticket.status === s ? 700 : 500,
                        color: sc.color, cursor: "pointer",
                        transition: "background 0.1s",
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = "#f9fafb"}
                      onMouseLeave={e => e.currentTarget.style.background = ticket.status === s ? "#f9fafb" : "#fff"}
                    >
                      {t(`support.status_${s}`) || s}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>
        {messages.length === 0 ? (
          <div style={{ textAlign: "center", color: "#9ca3af", fontSize: "13px", padding: "32px 0" }}>
            No messages yet.
          </div>
        ) : (
          messages.map((msg) => (
            <SupportMessage key={msg.message_id} message={msg} isAdmin={isAdmin} />
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* Reply area */}
      {!isClosed ? (
        <div style={{ borderTop: "1px solid #f3f4f6", padding: "14px 20px", background: "#fff" }}>
          {error && (
            <div style={{ fontSize: "12px", color: "#ef4444", marginBottom: "8px" }}>{error}</div>
          )}
          <Form onSubmit={handleSend}>
            <Form.Control
              as="textarea"
              rows={3}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={isAdmin ? t("support.admin_reply") : t("support.message_placeholder")}
              disabled={sending}
              style={{
                resize: "none", fontSize: "13px", marginBottom: "10px",
                borderRadius: "10px", border: "1px solid #e5e7eb",
              }}
            />
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", flexWrap: "wrap" }}>
              <SupportUploader files={files} onChange={setFiles} />
              <button
                type="submit"
                disabled={sending || !body.trim()}
                style={{
                  display: "inline-flex", alignItems: "center", gap: "6px",
                  padding: "7px 16px", borderRadius: "8px", border: "none",
                  background: (sending || !body.trim()) ? "#e5e7eb" : "#6366f1",
                  color: (sending || !body.trim()) ? "#9ca3af" : "#fff",
                  fontSize: "13px", fontWeight: 600,
                  cursor: (sending || !body.trim()) ? "not-allowed" : "pointer",
                  transition: "all 0.15s",
                }}
              >
                {sending ? <Spinner size="sm" /> : <>{t("support.send")}</>}
              </button>
            </div>
          </Form>
        </div>
      ) : (
        <div style={{
          borderTop: "1px solid #f3f4f6", padding: "14px 20px",
          textAlign: "center", fontSize: "12px", color: "#9ca3af",
          background: "#fafafa",
        }}>
          🔒 {t("support.status_closed")} — {isAdmin ? "Reopen the ticket to reply." : "This ticket is closed."}
        </div>
      )}
    </div>
  );
};

export default SupportChat;
